import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT, PRIVATE, PUBLIC, PAYMENT_CURRENCY, PUBLISHED, UNPUBLISHED,
} from "const"
import { CcLibrarySet, CcLibrarySetCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    CcLibrarySetValidation,
    SaveCcLibrarySet,
    GetMaxCcLibrarySets,
    IsLockedCcLibrarySet,
    closeModal,
    setCcLibrarySetEdit,
    setCcLibrarySetReloadList,
    setCcLibrarySetReloadView,
    setCcLibrarySetReloadEdit,
    LangType,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    FileLoader,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditComplianceSetProps {
    set: CcLibrarySet
}

let isMounted: boolean

const Form = ({
    set,
}: EditComplianceSetProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccLibrarySet.baseRoute)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [publicId, setPublicId] = useInput(set.publicId)
    const [name, setName] = useInput(set.name)
    const [description, setDescription] = useInput(set.description)

    const [logoId, setLogoId] = useState(set.logoId)
    const [logo, setLogo] = useState(set.logo)
    const [locked, setLocked] = useState(set.locked)
    const [access, setAccess] = useState(set.access)
    const [status, setStatus] = useState(set.status)
    const [cost, setCost] = useState(set.cost ? set.cost.toFixed(2) : "0")
    const [formal, setFormal] = useState(set.formal)
    const [isDefault, setIsDefault] = useState(set.default)
    const ownerAccount = set.ownerAccount || userData.accountId

    const [validation, setValidation] = useState<CcLibrarySetValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = set.id
            ? `${baseRoute}/${VIEW}/${set.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        let costParsed = parseFloat(cost) || 0
        if (costParsed < 0) costParsed = 0
        setCost(costParsed.toFixed(2))

        const newSetData: CcLibrarySetCreate = {
            originalSetId: set.originalSetId || null,
            accountId: set.accountId || userData.accountId,
            projectId: set.projectId || project.id,
            publicId,
            name,
            description,
            logoId,
            ownerAccount,
            creator: set.id ? set.creator : userData.userId,
            locked,
            access,
            status,
            cost: costParsed,
            formal,
            default: isDefault,
        }

        const { maxAccountSets } = GetMaxCcLibrarySets(packageSettings, userData)
        SaveCcLibrarySet(
            set.id,
            newSetData,
            maxAccountSets,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newSetId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newSetId}`)
            }
            if (res.validation) return

            dispatch(setCcLibrarySetEdit({
                id: res.newSetId || set.id,
                logo,
                ...newSetData,
            }))

            dispatch(setCcLibrarySetReloadList(false))
            dispatch(setCcLibrarySetReloadView(false))
            setTimeout(() => {
                dispatch(setCcLibrarySetReloadList(true))
                dispatch(setCcLibrarySetReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcLibrarySetReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcLibrarySetReloadEdit(true))
                }, 0)
            }
        })
    }

    const isLocked = IsLockedCcLibrarySet(project.id, set, userData)
    const isSuperAdminOwner = ownerAccount === userData.accountId && userData.accountId === 1

    return (
        <PageForm>
            <Row>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.publicId}</Label>
                        <Input type="text" value={publicId} onChange={setPublicId} readOnly={isLocked} />
                        <Feedback>{validation && validation.publicId}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={isLocked} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} readOnly={isLocked} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                </Col>

                <Col xs={!project.id ? 6 : 12}>
                    <FormGroup>
                        <Label>{lang.default}</Label>
                        <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                            <SelectDropdownItem onClick={() => setIsDefault(true)}>{lang.yes}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setIsDefault(false)}>{lang.no}</SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.formal}</Label>
                        <SelectDropdown title={formal ? lang.yes : lang.no}>
                            <SelectDropdownItem onClick={() => setFormal(true)}>{lang.yes}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setFormal(false)}>{lang.no}</SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.locked}</Label>
                        {
                            !project.id && ownerAccount === userData.accountId
                                ? (
                                    <SelectDropdown title={locked ? lang.yes : lang.no}>
                                        <SelectDropdownItem onClick={() => setLocked(true)}>{lang.yes}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => setLocked(false)}>{lang.no}</SelectDropdownItem>
                                    </SelectDropdown>
                                )
                                : <Input type="text" value={locked ? lang.yes : lang.no} readOnly />
                        }
                    </FormGroup>
                </Col>

                {
                    !project.id && (
                        <Col xs={6}>
                            <FormGroup>
                                <Label>{lang.access}</Label>
                                {
                                    isSuperAdminOwner && (
                                        <SelectDropdown title={lang[access as keyof LangType]}>
                                            <SelectDropdownItem onClick={() => setAccess(PRIVATE)}>{lang.private}</SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => setAccess(PUBLIC)}>{lang.public}</SelectDropdownItem>
                                        </SelectDropdown>
                                    )
                                }
                                {
                                    !isSuperAdminOwner
                                    && <Input type="text" value={lang[access as keyof LangType]} readOnly />
                                }
                            </FormGroup>
                            {
                                isSuperAdminOwner && (
                                    <FormGroup>
                                        <Label>{`${lang.cost}, ${PAYMENT_CURRENCY}`}</Label>
                                        <Input type="text" value={cost} onChange={(e) => setCost(e.target.value)} />
                                    </FormGroup>
                                )
                            }
                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <SelectDropdown title={lang[status as keyof LangType]}>
                                    <SelectDropdownItem onClick={() => setStatus(PUBLISHED)}>{lang.published}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setStatus(UNPUBLISHED)}>{lang.unpublished}</SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                    )
                }
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
